import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export interface PedagogyReviewDistributionSummary {
  totalCompleted: number
  queueCompleted: number
  manualCompleted: number
  queueSkipped: number
  studiosTotal: number
  studiosWithZero: number
  zeroStudiosInRedZone: number
  zeroStudiosAvailable: number
  pendingTotal: number
}

export interface PedagogyReviewDistributionPartnerRow {
  partnerNumber: number
  partnerName: string | null
  studiosCount: number
  completedTotal: number
  completedFromQueue: number
  completedManually: number
  seniorCompletedTotal: number
  skippedCount: number
  pendingCount: number
  completedPerStudio: number
  studiosWithZero: number
  zeroStudiosInRedZone: number
  goalStudiosCompleted: number
  goalStudiosRemaining: number
  lastReviewDate: string | null
  redZoneStudiosCount: number
  noAccessStudiosCount: number
}

export interface PedagogyReviewDistributionStudioRow {
  studioInternalId: number
  studioNumber: string
  cityId: number
  cityName: string | null
  partnerNumber: number
  partnerName: string | null
  isRedZone: boolean
  redZoneSince: string | null
  isNoAccess: boolean
  noAccessSince: string | null
  noAccessUntil: string | null
  lastReviewDate: string | null
  completedTotal: number
  completedFromQueue: number
  completedManually: number
  seniorCompletedTotal: number
  skippedCount: number
  pendingCount: number
  goalTarget: number
  goalRemaining: number
  goalCompleted: boolean
}

export interface PedagogyReviewDistribution {
  summary: PedagogyReviewDistributionSummary
  partners: PedagogyReviewDistributionPartnerRow[]
  studios: PedagogyReviewDistributionStudioRow[]
}

const numberValue = (value: unknown): number => Number(value ?? 0)
const nullableText = (value: unknown): string | null => value == null ? null : String(value)
type JsonRecord = Record<string, unknown>
const recordValue = (value: unknown): JsonRecord => value && typeof value === 'object' ? value as JsonRecord : {}

export async function getPedagogyReviewDistribution(input: {
  periodId: number
  weekNumber?: number | null
}): Promise<PedagogyReviewDistribution> {
  const { data, error } = await withAuthRecovery(() =>
    getSupabase().rpc('get_pedagogy_review_distribution_admin' as never, {
      p_period_id: input.periodId,
      p_week_number_in_period: input.weekNumber ?? null,
    } as never)
  )
  if (error) throw new Error(error.message)

  const payload = recordValue(data)
  const summary = recordValue(payload.summary)
  return {
    summary: {
      totalCompleted: numberValue(summary.totalCompleted),
      queueCompleted: numberValue(summary.queueCompleted),
      manualCompleted: numberValue(summary.manualCompleted),
      queueSkipped: numberValue(summary.queueSkipped),
      studiosTotal: numberValue(summary.studiosTotal),
      studiosWithZero: numberValue(summary.studiosWithZero),
      zeroStudiosInRedZone: numberValue(summary.zeroStudiosInRedZone),
      zeroStudiosAvailable: numberValue(summary.zeroStudiosAvailable),
      pendingTotal: numberValue(summary.pendingTotal),
    },
    partners: Array.isArray(payload.partners) ? payload.partners.map(row => {
      const item = recordValue(row)
      return {
        partnerNumber: numberValue(item.partner_number),
        partnerName: item.partner_name == null ? null : String(item.partner_name),
        studiosCount: numberValue(item.studios_count),
        completedTotal: numberValue(item.completed_total),
        completedFromQueue: numberValue(item.completed_from_queue),
        completedManually: numberValue(item.completed_manually),
        seniorCompletedTotal: numberValue(item.senior_completed_total),
        skippedCount: numberValue(item.skipped_count),
        pendingCount: numberValue(item.pending_count),
        completedPerStudio: numberValue(item.completed_per_studio),
        studiosWithZero: numberValue(item.studios_with_zero),
        zeroStudiosInRedZone: numberValue(item.zero_studios_in_red_zone),
        goalStudiosCompleted: numberValue(item.goal_studios_completed),
        goalStudiosRemaining: numberValue(item.goal_studios_remaining),
        lastReviewDate: nullableText(item.last_review_date),
        redZoneStudiosCount: numberValue(item.red_zone_studios_count),
        noAccessStudiosCount: numberValue(item.no_access_studios_count),
      }
    }) : [],
    studios: Array.isArray(payload.studios) ? payload.studios.map(row => {
      const item = recordValue(row)
      return {
        studioInternalId: numberValue(item.studio_internal_id),
        studioNumber: String(item.studio_number ?? ''),
        cityId: numberValue(item.city_id),
        cityName: item.city_name == null ? null : String(item.city_name),
        partnerNumber: numberValue(item.partner_number),
        partnerName: item.partner_name == null ? null : String(item.partner_name),
        isRedZone: Boolean(item.is_red_zone),
        redZoneSince: nullableText(item.red_zone_since),
        isNoAccess: Boolean(item.is_no_access),
        noAccessSince: nullableText(item.no_access_since),
        noAccessUntil: nullableText(item.no_access_until),
        lastReviewDate: nullableText(item.last_review_date),
        completedTotal: numberValue(item.completed_total),
        completedFromQueue: numberValue(item.completed_from_queue),
        completedManually: numberValue(item.completed_manually),
        seniorCompletedTotal: numberValue(item.senior_completed_total),
        skippedCount: numberValue(item.skipped_count),
        pendingCount: numberValue(item.pending_count),
        goalTarget: numberValue(item.goal_target) || 3,
        goalRemaining: numberValue(item.goal_remaining),
        goalCompleted: Boolean(item.goal_completed),
      }
    }) : [],
  }
}
